import {
  addDefaultProps,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { Follow } from 'shared/src/api/types'
import { trackEvent } from 'shared/src/utils/analytics'
import { addAuthContext } from 'shared/src/utils/auth'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { sansSerifFontFamily } from 'utils/styles'
import followIconFollowed from '../../../assets/follow-icon-followed.svg'
import followIconNavbarFollowed from '../../../assets/follow-icon-navbar-followed.svg'
import followIconNavbar from '../../../assets/follow-icon-navbar.svg'
import followIcon from '../../../assets/follow-icon.svg'
import { addShowRegisterContext } from '../../../utils/register'
import {
  addNotificationContext,
  createAddedToMyCreatorsNotification,
} from '../../notifications'

type Type = 'standalone' | 'navbar'

const getIcon = (type: Type, followed: boolean): string => {
  if (type === 'standalone') {
    if (followed) {
      return followIconFollowed
    } else {
      return followIcon
    }
  } else {
    if (followed) {
      return followIconNavbarFollowed
    } else {
      return followIconNavbar
    }
  }
}

const useStyles = createUseStyles({
  button: {
    height: 32,
    background: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(5px)',
    border: 'none',
    borderRadius: 16,
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '15px',
    letterSpacing: '0.1em',
    color: '#CBA67A',
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 20,
    paddingRight: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
})

interface Props {
  type?: Type
  creatorId: number
  className?: string
}

export const FollowButton: FC<Props> = flowMax(
  addDisplayName('FollowButton'),
  addDefaultProps({
    type: 'standalone',
  }),
  addAuthContext,
  addApi,
  addState('follow', 'setFollow', typedAs<Follow | null>(null)),
  addState('hasLoaded', 'setLoaded', false),
  addEffect(
    ({ creatorId, api, isAuthenticated, setFollow, setLoaded }) =>
      () => {
        if (!isAuthenticated) {
          setLoaded(true)
          return
        }

        api.follows
          .getForCreatorId(creatorId)
          .catch(() => null)
          .then((follow) => {
            setFollow(follow)
            setLoaded(true)
          })
      },
    ['creatorId', 'isAuthenticated']
  ),
  addNotificationContext,
  addShowRegisterContext,
  addTranslationHelpers,
  addHandlers({
    toggleFollow:
      ({
        follow,
        setFollow,
        creatorId,
        api,
        showNotification,
        showRegisterIfNotAuthenticated,
        t,
      }) =>
      () => {
        if (showRegisterIfNotAuthenticated('follow')) {
          return
        }

        if (follow) {
          api.follows.delete(follow.id)
          setFollow(null)
        } else {
          setFollow({ id: 0, creatorId })
          api.follows.create({ creatorId }).then(setFollow)

          showNotification(createAddedToMyCreatorsNotification(t))
          trackEvent(
            {
              category: 'profile',
              action: 'follow',
            },
            {
              creator_id: creatorId,
            }
          )
        }
      },
  }),
  addProps(({ type, hasLoaded, follow, t }) => ({
    icon: getIcon(type, !!follow),
    text: hasLoaded
      ? follow
        ? t('profile.unfollow')
        : t('profile.follow')
      : null,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ type, icon, text, toggleFollow, classes, className }) => (
    <button onClick={toggleFollow} className={cx(classes.button, className)}>
      <img
        src={icon}
        alt={type === 'standalone' ? undefined : text ?? undefined}
      />
      {type === 'standalone' ? text : null}
    </button>
  )
)
